import React, { useState, useMemo, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  FunnelIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  Squares2X2Icon,
  TableCellsIcon,
  TruckIcon,
  BuildingOfficeIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  CurrencyDollarIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  XCircleIcon,
  EllipsisVerticalIcon,
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { useSuppliers } from '../hooks/useSuppliers';
import SupplierForm from '../components/SupplierForm';
import {
  Supplier,
  CreateSupplierDto,
  UpdateSupplierDto,
  SupplierType,
  SupplierStatus,
  PaymentTerms,
  getSupplierStatusColor,
  getSupplierTypeColor,
  getPaymentTermsLabel,
  formatSupplierType,
  formatSupplierStatus,
} from '../api/suppliers';
import { transactionsApi, PaymentMethod } from '../api/transactions';
import { safeNumber } from '../utils/formatters';
import PageHero from '../components/ui/PageHero';
import EmptyState from '../components/ui/EmptyState';
import {
  BrandModal,
  BrandModalBody,
  BrandModalFooter,
  BrandModalHeader,
} from '../components/ui/BrandModal';
import AmountInput from '../components/ui/AmountInput';

interface SupplierFilters {
  type?: SupplierType;
  status?: SupplierStatus;
  city?: string;
  province?: string;
  paymentTerms?: PaymentTerms;
}

export default function Suppliers() {
  const {
    suppliers,
    statistics,
    loading,
    error,
    createSupplier,
    updateSupplier,
    deleteSupplier,
    refreshData,
  } = useSuppliers();

  // UI state
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<SupplierFilters>({});
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  const [sortBy] = useState<string>('name');
  const [sortOrder] = useState<'asc' | 'desc'>('asc');
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');

  // Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);

  // Filter and search logic
  const filteredSuppliers = useMemo(() => {
    let result = suppliers;

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (supplier) =>
          supplier.name.toLowerCase().includes(term) ||
          supplier.code.toLowerCase().includes(term) ||
          supplier.contactPerson?.toLowerCase().includes(term) ||
          supplier.email?.toLowerCase().includes(term) ||
          supplier.city?.toLowerCase().includes(term) ||
          supplier.province?.toLowerCase().includes(term)
      );
    }

    // Apply filters
    if (filters.type) {
      result = result.filter((supplier) => supplier.type === filters.type);
    }
    if (filters.status) {
      result = result.filter((supplier) => supplier.status === filters.status);
    }
    if (filters.city) {
      result = result.filter((supplier) => supplier.city === filters.city);
    }
    if (filters.province) {
      result = result.filter((supplier) => supplier.province === filters.province);
    }
    if (filters.paymentTerms) {
      result = result.filter((supplier) => supplier.paymentTerms === filters.paymentTerms);
    }

    // Apply sorting
    result.sort((a, b) => {
      let aVal: any = a[sortBy as keyof Supplier];
      let bVal: any = b[sortBy as keyof Supplier];
      
      if (aVal === undefined && bVal === undefined) return 0;
      if (aVal === undefined) return sortOrder === 'asc' ? 1 : -1;
      if (bVal === undefined) return sortOrder === 'asc' ? -1 : 1;
      
      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [suppliers, searchTerm, filters, sortBy, sortOrder]);

  // Pagination logic
  const totalPages = Math.ceil(filteredSuppliers.length / itemsPerPage);
  const paginatedSuppliers = filteredSuppliers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Get unique values for filter options
  const uniqueCities = [...new Set(suppliers.map((supplier) => supplier.city).filter(Boolean))];
  const uniqueProvinces = [...new Set(suppliers.map((supplier) => supplier.province).filter(Boolean))];

  // Event handlers
  const handleCreateSupplier = async (data: CreateSupplierDto | UpdateSupplierDto) => {
    const result = await createSupplier(data as CreateSupplierDto);
    if (result) {
      setShowAddModal(false);
    }
  };

  const handleUpdateSupplier = async (data: CreateSupplierDto | UpdateSupplierDto) => {
    if (selectedSupplier) {
      const result = await updateSupplier(selectedSupplier.id, data as UpdateSupplierDto);
      if (result) {
        setShowEditModal(false);
        setSelectedSupplier(null);
      }
    }
  };

  const handleDeleteSupplier = async () => {
    if (selectedSupplier) {
      const success = await deleteSupplier(selectedSupplier.id);
      if (success) {
        setShowDeleteModal(false);
        setSelectedSupplier(null);
      }
    }
  };

  const clearFilters = () => {
    setFilters({});
  };

  const openViewModal = (supplier: Supplier) => {
    setSelectedSupplier(supplier);
    setShowViewModal(true);
  };

  const openEditModal = (supplier: Supplier) => {
    setSelectedSupplier(supplier);
    setShowEditModal(true);
  };

  const openDeleteModal = (supplier: Supplier) => {
    setSelectedSupplier(supplier);
    setShowDeleteModal(true);
  };

  const openPaymentModal = (supplier: Supplier) => {
    setSelectedSupplier(supplier);
    setShowPaymentModal(true);
  };

  const getStatusBadge = (supplier: Supplier) => {
    const color = getSupplierStatusColor(supplier.status);
    const statusText = formatSupplierStatus(supplier.status);
    
    const colorClasses = {
      green: 'bg-emerald-100 text-emerald-800',
      yellow: 'bg-amber-100 text-amber-800',
      red: 'bg-rose-100 text-rose-800',
      gray: 'bg-violet-100 text-violet-800',
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClasses[color as keyof typeof colorClasses]}`}>
        {statusText}
      </span>
    );
  };

  const getTypeBadge = (supplier: Supplier) => {
    const color = getSupplierTypeColor(supplier.type);
    const typeText = formatSupplierType(supplier.type);
    
    const colorClasses = {
      green: 'bg-emerald-100 text-emerald-800',
      red: 'bg-rose-100 text-rose-800',
      blue: 'bg-cyan-100 text-cyan-800',
      purple: 'bg-purple-100 text-purple-800',
      orange: 'bg-orange-100 text-orange-800',
      gray: 'bg-violet-100 text-violet-800',
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClasses[color as keyof typeof colorClasses]}`}>
        {typeText}
      </span>
    );
  };

  // Statistics from hook - ensure proper numeric conversion
  const {
    totalSuppliers: rawTotalSuppliers = 0,
    activeSuppliers: rawActiveSuppliers = 0,
    inactiveSuppliers: rawInactiveSuppliers = 0,
    suspendedSuppliers: rawSuspendedSuppliers = 0,
    pendingSuppliers: rawPendingSuppliers = 0,
    totalOutstandingBalance: rawTotalOutstandingBalance = 0,
  } = statistics || {};

  // Convert to proper numbers to avoid string concatenation
  const totalSuppliers = safeNumber(rawTotalSuppliers);
  const activeSuppliers = safeNumber(rawActiveSuppliers);
  const inactiveSuppliers = safeNumber(rawInactiveSuppliers);
  const suspendedSuppliers = safeNumber(rawSuspendedSuppliers);
  const pendingSuppliers = safeNumber(rawPendingSuppliers);
  const totalOutstandingBalance = safeNumber(rawTotalOutstandingBalance);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="text-center">
          <h3 className="mt-2 text-sm font-semibold text-slate-900">Error</h3>
          <p className="mt-1 text-sm text-slate-500">{error}</p>
          <div className="mt-6">
            <button
              type="button"
              className="inline-flex items-center btn btn-primary text-sm shadow-sm"
              onClick={refreshData}
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <PageHero
        title="Suppliers"
        subtitle="Supplier and vendor management with contacts, terms, and performance tracking"
        actions={
          <button type="button" onClick={() => setShowAddModal(true)} className="btn btn-primary focus-pop">
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
            Add Supplier
          </button>
        }
      />

      {/* Statistics Overview */}
      <div className="mt-8 space-y-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center">
              <BuildingOfficeIcon className="h-5 w-5 text-cyan-600 mr-2" />
              <h2 className="text-lg font-semibold text-slate-900">Supplier Analytics</h2>
            </div>
          </div>
          
          <div className="p-6">
            {/* Primary Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="bg-cyan-50 p-6 rounded-lg border border-blue-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-cyan-600 mb-1">Total Suppliers</p>
                    <p className="text-3xl font-bold text-blue-900">{totalSuppliers}</p>
                    <p className="text-sm text-cyan-700 mt-1">Registered suppliers</p>
                  </div>
                  <div className="bg-blue-100 p-3 rounded-full">
                    <TruckIcon className="h-8 w-8 text-cyan-600" />
                  </div>
                </div>
              </div>

              <div className="bg-orange-50 p-6 rounded-lg border border-orange-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-orange-600 mb-1">Outstanding Balance</p>
                    <p className="text-3xl font-bold text-orange-900">
                      R{safeNumber(totalOutstandingBalance).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
                    </p>
                    <p className="text-sm text-orange-700 mt-1">Amount due to suppliers</p>
                  </div>
                  <div className="bg-orange-100 p-3 rounded-full">
                    <CurrencyDollarIcon className="h-8 w-8 text-orange-600" />
                  </div>
                </div>
              </div>
            </div>

            {/* Status Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white p-4 rounded-lg border border-gray-200 hover:shadow-sm transition-shadow">
                <div className="flex items-center">
                  <div className="bg-green-100 p-2 rounded-lg mr-3">
                    <CheckCircleIcon className="h-4 w-4 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-500">Active</p>
                    <p className="text-xl font-semibold text-green-600">{activeSuppliers}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-4 rounded-lg border border-gray-200 hover:shadow-sm transition-shadow">
                <div className="flex items-center">
                  <div className="bg-gray-100 p-2 rounded-lg mr-3">
                    <XCircleIcon className="h-4 w-4 text-slate-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-500">Inactive</p>
                    <p className="text-xl font-semibold text-slate-600">{inactiveSuppliers}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-4 rounded-lg border border-gray-200 hover:shadow-sm transition-shadow">
                <div className="flex items-center">
                  <div className="bg-red-100 p-2 rounded-lg mr-3">
                    <ExclamationTriangleIcon className="h-4 w-4 text-red-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-500">Suspended</p>
                    <p className="text-xl font-semibold text-red-600">{suspendedSuppliers}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-4 rounded-lg border border-gray-200 hover:shadow-sm transition-shadow">
                <div className="flex items-center">
                  <div className="bg-yellow-100 p-2 rounded-lg mr-3">
                    <ClockIcon className="h-4 w-4 text-yellow-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-500">Pending</p>
                    <p className="text-xl font-semibold text-yellow-600">{pendingSuppliers}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filter Controls */}
      <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex-1 max-w-lg">
          <div className="flex items-center w-full rounded-md ring-1 ring-inset ring-gray-300 bg-white focus-within:ring-2 focus-within:ring-inset focus-within:ring-cyan-500">
            <span className="pl-3 pr-2 flex items-center flex-shrink-0 text-slate-400" aria-hidden="true">
              <MagnifyingGlassIcon className="h-5 w-5" />
            </span>
            <input
              type="text"
              name="search"
              id="search"
              className="flex-1 min-w-0 py-1.5 pr-3 border-0 bg-transparent text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-0 sm:text-sm sm:leading-6"
              placeholder="Search suppliers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Filter Toggle */}
          <button
            type="button"
            className={`relative inline-flex items-center px-3 py-2 text-sm font-semibold ${
              showFilters
                ? 'bg-cyan-600 text-white'
                : 'bg-white text-slate-900 ring-1 ring-inset ring-gray-300 hover:bg-slate-50/80'
            } rounded-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-600`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <FunnelIcon className="h-4 w-4 mr-2" />
            Filters
            {Object.keys(filters).length > 0 && (
              <span className="ml-1 inline-flex items-center rounded-full bg-red-100 px-1.5 py-0.5 text-xs font-medium text-red-700">
                {Object.keys(filters).length}
              </span>
            )}
          </button>

          {/* View Mode Toggle */}
          <div className="flex rounded-md shadow-sm">
            <button
              type="button"
              className={`relative inline-flex items-center rounded-l-md px-3 py-2 text-sm font-semibold ring-1 ring-inset ring-gray-300 hover:bg-slate-50/80 focus:z-10 ${
                viewMode === 'grid' ? 'bg-cyan-600 text-white' : 'bg-white text-slate-900'
              }`}
              onClick={() => setViewMode('grid')}
            >
              <Squares2X2Icon className="h-4 w-4" />
            </button>
            <button
              type="button"
              className={`relative -ml-px inline-flex items-center rounded-r-md px-3 py-2 text-sm font-semibold ring-1 ring-inset ring-gray-300 hover:bg-slate-50/80 focus:z-10 ${
                viewMode === 'table' ? 'bg-cyan-600 text-white' : 'bg-white text-slate-900'
              }`}
              onClick={() => setViewMode('table')}
            >
              <TableCellsIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="mt-4 bg-slate-50/80 p-4 rounded-lg">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
            <div>
              <label className="block text-sm font-medium text-slate-700">Type</label>
              <select
                value={filters.type || ''}
                onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value as SupplierType || undefined }))}
                className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              >
                <option key="empty-option-1753461297403" value="">All Types</option>
                {Object.values(SupplierType).map((type) => (
                  <option key={type} value={type}>
                    {formatSupplierType(type)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700">Status</label>
              <select
                value={filters.status || ''}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value as SupplierStatus || undefined }))}
                className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              >
                <option key="empty-option-1753461297403" value="">All Statuses</option>
                {Object.values(SupplierStatus).map((status) => (
                  <option key={status} value={status}>
                    {formatSupplierStatus(status)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700">City</label>
              <select
                value={filters.city || ''}
                onChange={(e) => setFilters(prev => ({ ...prev, city: e.target.value || undefined }))}
                className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              >
                <option key="empty-option-1753461297403" value="">All Cities</option>
                {uniqueCities.map((city) => (
                  <option key={city} value={city}>
                    {city}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700">Province</label>
              <select
                value={filters.province || ''}
                onChange={(e) => setFilters(prev => ({ ...prev, province: e.target.value || undefined }))}
                className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              >
                <option key="empty-option-1753461297403" value="">All Provinces</option>
                {uniqueProvinces.map((province) => (
                  <option key={province} value={province}>
                    {province}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-end">
              <button
                type="button"
                onClick={clearFilters}
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50/80"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Suppliers Grid/Table View */}
      {filteredSuppliers.length === 0 ? (
        <EmptyState
          title="No suppliers found"
          description={
            searchTerm || Object.keys(filters).length > 0
              ? 'Try adjusting your search or filters.'
              : 'Get started by adding your first supplier.'
          }
          icon={<TruckIcon className="h-6 w-6" />}
          action={
            !searchTerm && Object.keys(filters).length === 0 ? (
              <button type="button" onClick={() => setShowAddModal(true)} className="btn btn-primary text-sm focus-pop">
                <PlusIcon className="h-5 w-5" />
                Add Supplier
              </button>
            ) : undefined
          }
        />
      ) : (
        <>
          {viewMode === 'grid' ? (
            <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {paginatedSuppliers.map((supplier) => (
                <div key={supplier.id} className="bg-white overflow-hidden shadow rounded-lg hover:shadow-md transition-shadow">
                  <div className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="flex-shrink-0">
                          <BuildingOfficeIcon className="h-8 w-8 text-slate-400" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-slate-900 truncate">{supplier.name}</p>
                          <p className="text-sm text-slate-500">{supplier.code}</p>
                        </div>
                      </div>
                      <div className="flex space-x-1">
                        <button
                          onClick={() => openViewModal(supplier)}
                          className="text-slate-400 hover:text-slate-500"
                        >
                          <EyeIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => openEditModal(supplier)}
                          className="text-indigo-400 hover:text-cyan-500"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => openDeleteModal(supplier)}
                          className="text-red-400 hover:text-red-500"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </div>

                    <div className="mt-4 space-y-2">
                      <div className="flex justify-between">
                        {getTypeBadge(supplier)}
                        {getStatusBadge(supplier)}
                      </div>

                      {supplier.contactPerson && (
                        <div className="flex items-center text-sm text-slate-500">
                          <span className="truncate">{supplier.contactPerson}</span>
                        </div>
                      )}

                      {supplier.email && (
                        <div className="flex items-center text-sm text-slate-500">
                          <EnvelopeIcon className="h-4 w-4 mr-2 flex-shrink-0" />
                          <span className="truncate">{supplier.email}</span>
                        </div>
                      )}

                      {supplier.phone && (
                        <div className="flex items-center text-sm text-slate-500">
                          <PhoneIcon className="h-4 w-4 mr-2 flex-shrink-0" />
                          <span>{supplier.phone}</span>
                        </div>
                      )}

                      {(supplier.city || supplier.province) && (
                        <div className="flex items-center text-sm text-slate-500">
                          <MapPinIcon className="h-4 w-4 mr-2 flex-shrink-0" />
                          <span className="truncate">
                            {[supplier.city, supplier.province].filter(Boolean).join(', ')}
                          </span>
                        </div>
                      )}

                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-500">Payment Terms:</span>
                        <span className="font-medium">{getPaymentTermsLabel(supplier.paymentTerms)}</span>
                      </div>

                      {supplier.currentBalance !== 0 && (
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-slate-500">Balance:</span>
                          <span className={`font-medium ${supplier.currentBalance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                            R{safeNumber(Math.abs(supplier.currentBalance)).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
                            {supplier.currentBalance > 0 ? ' (Owed)' : ' (Credit)'}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="mt-8 bg-white shadow overflow-hidden sm:rounded-md">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-slate-50/80">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Supplier
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Location
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Payment Terms
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Balance
                    </th>
                    <th className="relative px-6 py-3">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedSuppliers.map((supplier) => (
                    <tr key={supplier.id} className="hover:bg-slate-50/80">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <BuildingOfficeIcon className="h-10 w-10 text-slate-400" />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-slate-900">{supplier.name}</div>
                            <div className="text-sm text-slate-500">{supplier.code}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getTypeBadge(supplier)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(supplier)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-slate-900">{supplier.contactPerson || '-'}</div>
                        <div className="text-sm text-slate-500">{supplier.email || '-'}</div>
                        <div className="text-sm text-slate-500">{supplier.phone || '-'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-slate-900">
                          {[supplier.city, supplier.province].filter(Boolean).join(', ') || '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                        {getPaymentTermsLabel(supplier.paymentTerms)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {supplier.currentBalance !== 0 ? (
                          <span className={`text-sm font-medium ${supplier.currentBalance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                            R{safeNumber(Math.abs(supplier.currentBalance)).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
                            {supplier.currentBalance > 0 ? ' (Owed)' : ' (Credit)'}
                          </span>
                        ) : (
                          <span className="text-sm text-slate-500">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => openViewModal(supplier)}
                            className="text-slate-400 hover:text-slate-500"
                          >
                            <EyeIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => openEditModal(supplier)}
                            className="text-cyan-600 hover:text-indigo-900"
                          >
                            <PencilIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => openDeleteModal(supplier)}
                            className="text-red-600 hover:text-red-900"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                          {supplier.currentBalance > 0 && (
                            <button
                              onClick={() => openPaymentModal(supplier)}
                              className="text-green-600 hover:text-green-900"
                              title="Pay Supplier"
                            >
                              <CurrencyDollarIcon className="h-5 w-5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-6 flex items-center justify-between">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-slate-700 bg-white hover:bg-slate-50/80 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-slate-700 bg-white hover:bg-slate-50/80 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-slate-700">
                    Showing <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> to{' '}
                    <span className="font-medium">
                      {Math.min(currentPage * itemsPerPage, filteredSuppliers.length)}
                    </span>{' '}
                    of <span className="font-medium">{filteredSuppliers.length}</span> results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-slate-500 hover:bg-slate-50/80 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeftIcon className="h-5 w-5" />
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          page === currentPage
                            ? 'z-10 bg-cyan-50 border-cyan-500 text-cyan-600'
                            : 'bg-white border-gray-300 text-slate-500 hover:bg-slate-50/80'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-slate-500 hover:bg-slate-50/80 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRightIcon className="h-5 w-5" />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Add Supplier Modal */}
      <SupplierForm
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSubmit={handleCreateSupplier}
        title="Add New Supplier"
      />

      {/* Edit Supplier Modal */}
      <SupplierForm
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedSupplier(null);
        }}
        onSubmit={handleUpdateSupplier}
        supplier={selectedSupplier}
        title="Edit Supplier"
      />

      {/* View Supplier Modal */}
      <BrandModal open={showViewModal} onClose={() => setShowViewModal(false)} maxWidthClass="max-w-4xl">
        <BrandModalHeader title="Supplier Details" onClose={() => setShowViewModal(false)} />
        <BrandModalBody>
          {selectedSupplier && (
            <div className="space-y-6">
              {/* Basic Information */}
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-slate-700">Supplier Name</label>
                  <p className="mt-1 text-sm text-slate-900">{selectedSupplier.name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700">Supplier Code</label>
                  <p className="mt-1 text-sm text-slate-900">{selectedSupplier.code}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700">Type</label>
                  <div className="mt-1">{getTypeBadge(selectedSupplier)}</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700">Status</label>
                  <div className="mt-1">{getStatusBadge(selectedSupplier)}</div>
                </div>
              </div>

              {/* Contact Information */}
              <div className="border-t pt-6">
                <h4 className="text-lg font-medium text-slate-900 mb-4">Contact Information</h4>
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-slate-700">Contact Person</label>
                    <p className="mt-1 text-sm text-slate-900">{selectedSupplier.contactPerson || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700">Email</label>
                    <p className="mt-1 text-sm text-slate-900">{selectedSupplier.email || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700">Phone</label>
                    <p className="mt-1 text-sm text-slate-900">{selectedSupplier.phone || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700">Alternative Phone</label>
                    <p className="mt-1 text-sm text-slate-900">{selectedSupplier.alternativePhone || '-'}</p>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-slate-700">Website</label>
                    <p className="mt-1 text-sm text-slate-900">
                      {selectedSupplier.website ? (
                        <a href={selectedSupplier.website} target="_blank" rel="noopener noreferrer" className="text-cyan-600 hover:text-cyan-500">
                          {selectedSupplier.website}
                        </a>
                      ) : '-'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Address Information */}
              <div className="border-t pt-6">
                <h4 className="text-lg font-medium text-slate-900 mb-4">Address Information</h4>
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-slate-700">Address</label>
                    <p className="mt-1 text-sm text-slate-900">{selectedSupplier.address || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700">City</label>
                    <p className="mt-1 text-sm text-slate-900">{selectedSupplier.city || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700">Province</label>
                    <p className="mt-1 text-sm text-slate-900">{selectedSupplier.province || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700">Postal Code</label>
                    <p className="mt-1 text-sm text-slate-900">{selectedSupplier.postalCode || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700">Country</label>
                    <p className="mt-1 text-sm text-slate-900">{selectedSupplier.country || '-'}</p>
                  </div>
                </div>
              </div>

              {/* Business Information */}
              <div className="border-t pt-6">
                <h4 className="text-lg font-medium text-slate-900 mb-4">Business Information</h4>
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-slate-700">Tax/VAT Number</label>
                    <p className="mt-1 text-sm text-slate-900">{selectedSupplier.taxNumber || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700">Registration Number</label>
                    <p className="mt-1 text-sm text-slate-900">{selectedSupplier.registrationNumber || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700">Payment Terms</label>
                    <p className="mt-1 text-sm text-slate-900">{getPaymentTermsLabel(selectedSupplier.paymentTerms)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700">Credit Limit</label>
                    <p className="mt-1 text-sm text-slate-900">
                      {selectedSupplier.creditLimit ? `R${selectedSupplier.creditLimit.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}` : '-'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700">Current Balance</label>
                    <p className={`mt-1 text-sm font-medium ${selectedSupplier.currentBalance > 0 ? 'text-red-600' : selectedSupplier.currentBalance < 0 ? 'text-green-600' : 'text-slate-900'}`}>
                      {selectedSupplier.currentBalance !== 0 ? (
                        <>
                          R{safeNumber(Math.abs(selectedSupplier.currentBalance)).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
                          {selectedSupplier.currentBalance > 0 ? ' (Owed)' : ' (Credit)'}
                        </>
                      ) : 'R0.00'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Banking Information */}
              {(selectedSupplier.bankName || selectedSupplier.bankAccountNumber || selectedSupplier.bankBranchCode) && (
                <div className="border-t pt-6">
                  <h4 className="text-lg font-medium text-slate-900 mb-4">Banking Information</h4>
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
                    <div>
                      <label className="block text-sm font-medium text-slate-700">Bank Name</label>
                      <p className="mt-1 text-sm text-slate-900">{selectedSupplier.bankName || '-'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700">Account Number</label>
                      <p className="mt-1 text-sm text-slate-900">{selectedSupplier.bankAccountNumber || '-'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700">Branch Code</label>
                      <p className="mt-1 text-sm text-slate-900">{selectedSupplier.bankBranchCode || '-'}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Description */}
              {selectedSupplier.description && (
                <div className="border-t pt-6">
                  <label className="block text-sm font-medium text-slate-700">Description</label>
                  <p className="mt-1 text-sm text-slate-900">{selectedSupplier.description}</p>
                </div>
              )}

              {/* Timestamps */}
              <div className="border-t pt-6">
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-slate-700">Created</label>
                    <p className="mt-1 text-sm text-slate-900">
                      {format(new Date(selectedSupplier.createdAt), 'PPP p')}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700">Last Updated</label>
                    <p className="mt-1 text-sm text-slate-900">
                      {format(new Date(selectedSupplier.updatedAt), 'PPP p')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </BrandModalBody>
        <BrandModalFooter>
          <button
            type="button"
            className="btn btn-secondary text-sm"
            onClick={() => setShowViewModal(false)}
          >
            Close
          </button>
          <button
            type="button"
            className="btn btn-primary text-sm"
            onClick={() => {
              setShowViewModal(false);
              openEditModal(selectedSupplier!);
            }}
          >
            Edit Supplier
          </button>
        </BrandModalFooter>
      </BrandModal>

      {/* Delete Confirmation Modal */}
      <BrandModal
        open={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedSupplier(null);
        }}
        maxWidthClass="max-w-md"
      >
        <BrandModalHeader
          title="Delete Supplier"
          onClose={() => {
            setShowDeleteModal(false);
            setSelectedSupplier(null);
          }}
        />
        <BrandModalBody>
          <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
            <ExclamationTriangleIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
          </div>
          <p className="text-sm text-slate-600 text-center">
            Are you sure you want to delete &quot;{selectedSupplier?.name}&quot;? This action cannot be undone.
          </p>
        </BrandModalBody>
        <BrandModalFooter>
          <button
            type="button"
            className="btn btn-secondary text-sm"
            onClick={() => {
              setShowDeleteModal(false);
              setSelectedSupplier(null);
            }}
          >
            Cancel
          </button>
          <button type="button" className="btn btn-danger text-sm" onClick={handleDeleteSupplier}>
            Delete
          </button>
        </BrandModalFooter>
      </BrandModal>

      {/* Supplier Payment Modal */}
      <SupplierPaymentModal 
        isOpen={showPaymentModal}
        onClose={() => {
          setShowPaymentModal(false);
          setSelectedSupplier(null);
        }}
        supplier={selectedSupplier}
        onPaymentComplete={() => {
          refreshData();
          setShowPaymentModal(false);
          setSelectedSupplier(null);
        }}
      />
    </div>
  );
}

// Supplier Payment Modal Component
interface SupplierPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  supplier: Supplier | null;
  onPaymentComplete: () => void;
}

function SupplierPaymentModal({ isOpen, onClose, supplier, onPaymentComplete }: SupplierPaymentModalProps) {
  const [paymentData, setPaymentData] = useState({
    amount: 0,
    paymentMethod: PaymentMethod.BANK_TRANSFER,
    paymentDate: new Date().toISOString().split('T')[0],
    description: '',
    notes: '',
    reference: '',
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  // Reset form when supplier changes
  useEffect(() => {
    if (supplier) {
      setPaymentData({
        amount: supplier.currentBalance,
        paymentMethod: PaymentMethod.BANK_TRANSFER,
        paymentDate: new Date().toISOString().split('T')[0],
        description: `Payment to ${supplier.name}`,
        notes: '',
        reference: '',
      });
      setErrors([]);
    }
  }, [supplier]);

  const validateForm = (): boolean => {
    const newErrors: string[] = [];

    if (!supplier) {
      newErrors.push('No supplier selected');
      setErrors(newErrors);
      return false;
    }

    if (paymentData.amount <= 0) {
      newErrors.push('Payment amount must be greater than 0');
    }

    if (paymentData.amount > supplier.currentBalance) {
      newErrors.push(`Payment amount cannot exceed outstanding balance of R${supplier.currentBalance}`);
    }

    if (!paymentData.paymentDate) {
      newErrors.push('Payment date is required');
    }

    if (!paymentData.description.trim()) {
      newErrors.push('Description is required');
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm() || !supplier) return;

    setIsProcessing(true);

    try {
      await transactionsApi.processSupplierPayment({
        supplierId: supplier.id,
        amount: paymentData.amount,
        paymentMethod: paymentData.paymentMethod,
        paymentDate: new Date(paymentData.paymentDate),
        description: paymentData.description,
        notes: paymentData.notes,
        reference: paymentData.reference,
      });

      toast.success(`Payment of R${paymentData.amount.toLocaleString('en-ZA', { minimumFractionDigits: 2 })} to ${supplier.name} processed successfully!`);
      onPaymentComplete();
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Failed to process payment. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setPaymentData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear errors when user starts typing
    if (errors.length > 0) {
      setErrors([]);
    }
  };

  if (!supplier) return null;

  return (
    <BrandModal open={isOpen} onClose={onClose} maxWidthClass="max-w-md">
      <BrandModalHeader
        title="Pay Supplier"
        subtitle={`${supplier.name} - Outstanding: R${supplier.currentBalance.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}`}
        onClose={onClose}
      />
      <form onSubmit={handleSubmit}>
        <BrandModalBody>
          {errors.length > 0 && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <ul className="text-sm text-red-600 space-y-1">
                {errors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="space-y-4">
            {/* Payment Amount */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Payment Amount *
              </label>
              <AmountInput
                value={paymentData.amount}
                onChange={(v) => handleInputChange('amount', v ?? 0)}
                min={0}
                max={supplier.currentBalance}
                prefix="R"
                required
                inputClassName="block w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
              />
            </div>

            {/* Payment Method */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Payment Method *
              </label>
              <select
                value={paymentData.paymentMethod}
                onChange={(e) => handleInputChange('paymentMethod', e.target.value as PaymentMethod)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                required
              >
                <option value={PaymentMethod.BANK_TRANSFER}>Bank Transfer</option>
                <option value={PaymentMethod.CASH}>Cash</option>
                <option value={PaymentMethod.CHECK}>Check</option>
                <option value={PaymentMethod.CREDIT_CARD}>Credit Card</option>
              </select>
            </div>

            {/* Payment Date */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Payment Date *
              </label>
              <input
                type="date"
                value={paymentData.paymentDate}
                onChange={(e) => handleInputChange('paymentDate', e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                required
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Description *
              </label>
              <input
                type="text"
                value={paymentData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                placeholder="Payment description"
                required
              />
            </div>

            {/* Reference */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Reference
              </label>
              <input
                type="text"
                value={paymentData.reference}
                onChange={(e) => handleInputChange('reference', e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                placeholder="Payment reference number"
              />
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Notes
              </label>
              <textarea
                value={paymentData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                rows={3}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                placeholder="Additional notes"
              />
            </div>
          </div>
        </BrandModalBody>
        <BrandModalFooter>
          <button
            type="button"
            className="btn btn-secondary text-sm"
            onClick={onClose}
            disabled={isProcessing}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isProcessing}
          >
            {isProcessing ? 'Processing...' : 'Process Payment'}
          </button>
        </BrandModalFooter>
      </form>
    </BrandModal>
  );
} 